import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';

const CookieConsent = () => {
  const { cookieConsent, updateCookieConsent } = useCRM();
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selections, setSelections] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Only show if explicitly NOT accepted yet
    if (!cookieConsent?.accepted) {
      const timer = setTimeout(() => setShowBanner(true), 1500);
      return () => clearTimeout(timer);
    } else {
      setShowBanner(false);
    }
  }, [cookieConsent]);

  const handleAcceptAll = () => {
    // VIKTIG: Send hele objektet, ikke bare arrayet
    updateCookieConsent({
      accepted: true,
      types: ['necessary', 'functional', 'analytics', 'marketing'],
      date: new Date().toISOString()
    });
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const types = Object.keys(selections).filter(key => selections[key]);
    updateCookieConsent({
      accepted: true,
      types: types,
      date: new Date().toISOString()
    });
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }} className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[380px] bg-white rounded-2xl shadow-2xl border border-gray-200 z-[200] overflow-hidden">
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <SafeIcon icon="🛡️" className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Vi bryr oss om ditt personvern</h3>
              <p className="text-sm text-gray-600 mt-1">
                Vi bruker informasjonskapsler for å forbedre din opplevelse, analysere trafikk og for markedsføring.
              </p>
            </div>
          </div>

          {showDetails && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-6 space-y-3 border-t pt-4">
              {[
                { id: 'necessary', label: 'Nødvendige', desc: 'Kreves for at siden skal fungere.', required: true },
                { id: 'functional', label: 'Funksjonelle', desc: 'Husk dine valg og innstillinger.' },
                { id: 'analytics', label: 'Analyse', desc: 'Hjelper oss å forstå hvordan siden brukes.' },
                { id: 'marketing', label: 'Markedsføring', desc: 'Brukes for å vise relevante annonser.' }
              ].map((type) => (
                <div key={type.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                  <div>
                    <span className="text-sm font-semibold text-gray-800">{type.label}</span>
                    <p className="text-xs text-gray-500">{type.desc}</p>
                  </div>
                  <input type="checkbox" disabled={type.required} checked={selections[type.id]} onChange={(e) => setSelections({ ...selections, [type.id]: e.target.checked })} className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50" />
                </div>
              ))}
            </motion.div>
          )}

          <div className="mt-6 flex flex-col space-y-2">
            <div className="flex space-x-2">
              <button onClick={handleAcceptAll} className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl font-bold hover:bg-blue-700 transition-colors text-sm">
                Godta alle
              </button>
              <button onClick={() => setShowDetails(!showDetails)} className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-bold hover:bg-gray-200 transition-colors text-sm">
                <SafeIcon icon="⚙️" />
              </button>
            </div>
            {showDetails ? (
              <button onClick={handleSavePreferences} className="w-full border border-gray-200 text-gray-700 py-2 rounded-xl font-medium hover:bg-gray-50 transition-colors text-xs">
                Lagre mine valg
              </button>
            ) : (
              <button onClick={() => {
                updateCookieConsent({ accepted: true, types: ['necessary'], date: new Date().toISOString() });
                setShowBanner(false);
              }} className="w-full text-gray-500 py-2 font-medium hover:text-gray-700 transition-colors text-xs">
                Bare nødvendige
              </button>
            )}
          </div>
          <div className="mt-4 flex items-center justify-center space-x-4 text-[10px] text-gray-400">
            <a href="#" className="hover:underline">Personvernerklæring</a>
            <span>•</span>
            <a href="#" className="hover:underline">Cookie-policy</a>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;